import { useCallback, useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useAlbumActions } from '@/hooks/useAlbumActions';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUserPictures } from '@/hooks/useUserPictures';
import { useToast } from '@/hooks/useToast';
import { type AlbumMeta } from '@/lib/album-utils';
import { getImageUrl, getCaption } from '@/lib/polaroid-utils';
import { cn } from '@/lib/utils';

interface EditAlbumDialogProps {
  album: AlbumMeta;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditAlbumDialog({ album, open, onOpenChange }: EditAlbumDialogProps) {
  const { user } = useCurrentUser();
  const { data: userPictures, isLoading: picturesLoading } = useUserPictures(user?.pubkey, 100);
  const { updateAlbum } = useAlbumActions();
  const { toast } = useToast();

  const [title, setTitle] = useState(album.title);
  const [description, setDescription] = useState(album.description ?? '');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(album.photoIds));
  const [isSaving, setIsSaving] = useState(false);

  // Sync state when album prop changes
  useEffect(() => {
    setTitle(album.title);
    setDescription(album.description ?? '');
    setSelectedIds(new Set(album.photoIds));
  }, [album]);

  const togglePhoto = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSave = async () => {
    if (!title.trim() || !user) return;

    setIsSaving(true);
    try {
      // Use the first selected photo as the cover image if we need to update
      const firstSelectedId = [...selectedIds][0];
      const coverEvent = userPictures?.find((e) => e.id === firstSelectedId);
      const coverImage = coverEvent ? getImageUrl(coverEvent) : album.image;

      await updateAlbum({
        dTag: album.dTag,
        title: title.trim(),
        description: description.trim() || undefined,
        image: coverImage,
        photoIds: [...selectedIds],
        themeCoordinate: album.themeCoordinate,
      });

      toast({ title: 'Album updated!' });
      onOpenChange(false);
    } catch (error) {
      console.error('Update album failed:', error);
      toast({ title: 'Failed to update album', description: 'Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  /** Ruled notebook lines */
  const RULED_LINES = 'repeating-linear-gradient(to bottom, transparent, transparent 27px, #C8D8E8 27px, #C8D8E8 28px)';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0 bg-polaroid-white max-h-[85vh] overflow-hidden">
        <div className="flex flex-col max-h-[85vh]">
        {/* Sticky header with title + save button */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0 border-b border-cream-dark/30">
          <DialogHeader className="p-0">
            <DialogTitle className="font-hand text-2xl text-pol-text">Edit Album</DialogTitle>
          </DialogHeader>
          <button
            onClick={handleSave}
            disabled={!title.trim() || isSaving}
            className={cn(
              'font-hand text-base font-bold px-4 py-1.5 bg-pol-pink text-white transition-all active:scale-95',
              'disabled:opacity-40 disabled:cursor-not-allowed',
            )}
          >
            {isSaving ? 'Saving...' : `Save${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-5 pb-5 pt-4 space-y-4">
            {/* Title input */}
            <div>
              <label className="text-[13px] font-semibold uppercase tracking-wide text-pol-text mb-1.5 block">
                Album Name
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Summer 2025..."
                className="w-full bg-white font-hand text-lg text-pol-text py-2.5 px-3 outline-none border border-cream-dark focus:border-pol-pink/50 transition-colors placeholder:text-pol-text-secondary/40"
                style={{ backgroundImage: RULED_LINES, backgroundPosition: '0 8px' }}
              />
            </div>

            {/* Description input */}
            <div>
              <label className="text-[13px] font-semibold uppercase tracking-wide text-pol-text mb-1.5 flex items-center gap-1.5">
                Description
                <span className="text-pol-text-secondary/50 font-normal normal-case tracking-normal text-[11px]">
                  (optional)
                </span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's this album about..."
                rows={2}
                className="w-full bg-white font-hand text-base text-pol-text py-2.5 px-3 outline-none resize-none border border-cream-dark focus:border-pol-pink/50 transition-colors placeholder:text-pol-text-secondary/40"
                style={{ backgroundImage: RULED_LINES, backgroundPosition: '0 8px' }}
              />
            </div>

            {/* Photo picker */}
            <div>
              <label className="text-[13px] font-semibold uppercase tracking-wide text-pol-text mb-1.5 block">
                Photos
                {selectedIds.size > 0 && (
                  <span className="text-pol-pink font-normal normal-case tracking-normal text-[11px] ml-2">
                    {selectedIds.size} selected
                  </span>
                )}
              </label>

              {picturesLoading ? (
                <div className="grid grid-cols-3 gap-1.5">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="aspect-square skeleton-shimmer animate-shimmer" />
                  ))}
                </div>
              ) : userPictures && userPictures.length > 0 ? (
                <div className="grid grid-cols-3 gap-1.5">
                  {userPictures.map((event) => (
                    <PhotoPickerItem
                      key={event.id}
                      event={event}
                      selected={selectedIds.has(event.id)}
                      onToggle={togglePhoto}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center">
                  <p className="font-hand text-pol-text-secondary">No photos available</p>
                </div>
              )}
            </div>

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={!title.trim() || isSaving}
              className={cn(
                'relative w-full bg-polaroid-white py-3.5 shadow-[0_3px_12px_rgba(45,27,78,0.12)]',
                'transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(45,27,78,0.15)]',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_3px_12px_rgba(45,27,78,0.12)]',
              )}
            >
              <div
                className="absolute top-0 left-1/2 z-10 w-[80px] h-[18px] tape-rainbow"
                style={{ transform: 'translateX(-50%) translateY(-9px) rotate(1.5deg)' }}
              />
              <span className="relative z-10 font-hand text-xl font-bold text-pol-text">
                {isSaving ? 'Saving...' : 'Save Changes'}
              </span>
              <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-pol-pink opacity-50" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Small photo thumbnail for the picker grid */
function PhotoPickerItem({ event, selected, onToggle }: {
  event: NostrEvent;
  selected: boolean;
  onToggle: (id: string) => void;
}) {
  const imageUrl = getImageUrl(event);
  const caption = getCaption(event);

  if (!imageUrl) return null;

  return (
    <button
      onClick={() => onToggle(event.id)}
      className={cn(
        'relative aspect-square overflow-hidden transition-all',
        selected
          ? 'ring-2 ring-pol-pink ring-offset-1 ring-offset-polaroid-white scale-[0.96]'
          : 'hover:brightness-90 active:scale-95',
      )}
    >
      <img
        src={imageUrl}
        alt={caption || 'Photo'}
        className="w-full h-full object-cover"
        loading="lazy"
      />

      {/* Selected checkmark overlay */}
      {selected && (
        <div className="absolute top-1 right-1 w-5 h-5 bg-pol-pink flex items-center justify-center shadow-sm">
          <Check size={14} className="text-white" strokeWidth={3} />
        </div>
      )}
    </button>
  );
}
